import { SyntheticEvent } from 'react';
import CustomButton from '../../../common/components/CustomButton';
import ErrorMessage from '../../../common/components/ErrorMessage';
import { FieldValidationErrorsType } from '../../../common/form/FrekulForm';

type KaraokeUploadFormView2FieldName = 'background_order'|'offvocal_order'|'lyrics';
type KaraokeUploadFormView2FileName = 'song_vocal_off'|'song_vocal_on'|'movie';

// 地のフォーム
export default function KaraokeUploadFormView2({
  getValue,
  getErrors,
  getFiles,
  onUploadVideo,
  onUploadOffvocalAudio,
  onUploadOnvocalAudio,
  onUploadLyrics,
  onBack,
  onNext,
} : {
  getValue: (name: KaraokeUploadFormView2FieldName) => string,
  getFiles: (name: KaraokeUploadFormView2FileName) => FileList|null,
  getErrors: (name: KaraokeUploadFormView2FieldName|KaraokeUploadFormView2FileName) => FieldValidationErrorsType,
  onUploadVideo: () => void,
  onUploadOffvocalAudio: () => void,
  onUploadOnvocalAudio: () => void,
  onUploadLyrics: () => void,
  onNext: () => void,
  onBack: () => void,
}) {
  const needsVideoFiles = (getValue('background_order') === 'file');
  const needsOnvocalFiles = (getValue('background_order') === 'auto');
  const needsOffvocalFiles = (getValue('offvocal_order') !== 'vocal_removal');

  const fileUploaded = (files: FileList|null) => (files !== null && files.length > 0);
  const hasLyrics = getValue('lyrics') !== '';
  const hasOffvocalFile = fileUploaded(getFiles('song_vocal_off'));
  const hasOnvocalFile = fileUploaded(getFiles('song_vocal_on'));
  const hasVideoFile = fileUploaded(getFiles('movie'));
  const disabled = 
    (needsVideoFiles && !hasVideoFile) ||
    (needsOnvocalFiles && !hasOnvocalFile) ||
    (needsOffvocalFiles && !hasOffvocalFile) ||
    getValue('lyrics').length === 0;

  const cancelEvent = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <h3>
        以下の素材をご用意いただき、
        <br className="sp" />
        アップロードしてください。
      </h3>
      <p className="text_info t-center mt--20">
        スマートフォンからうまくアップロードできない
        <br className="sp" />
        場合は、パソコンでお試しください。
      </p>
      <form>
        <ul className="upload_list">
          {needsVideoFiles && (
            <>
              { /* eslint-disable-next-line max-len */ }
              { /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */ }
              <li className={hasVideoFile ? 'ok' : ''} onClick={(e) => { cancelEvent(e); onUploadVideo(); }}>
                <a href="/">
                  <b>ミュージックビデオ</b>
                  <span>
                  mp4(AVC), mov, wmvいずれかの形式。
                    <br />
                    1.3GB以内
                    <br />
                  </span>
                  <div className="label">
                    <p>{hasVideoFile ? '設定済み' : '未設定'}</p>
                  </div>
                  <div className="sub_button">アップロードする</div>
                </a>
                <ErrorMessage type="error" validationErrors={getErrors('movie')} />
              </li>
            </>
          )}
          {needsOnvocalFiles && (
            <>
              { /* eslint-disable-next-line max-len */ }
              { /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */ }
              <li className={hasOnvocalFile ? 'ok' : ''} onClick={(e) => { cancelEvent(e); onUploadOnvocalAudio(); }}>
                <a href="/">
                  <b>歌あり音源</b>
                  <span>
                    wavまたはmp3形式。
                    <br />
                    150MB以内
                  </span>
                  <div className="label">
                    <p>{hasOnvocalFile ? '設定済み' : '未設定'}</p>
                  </div>
                  <div className="sub_button">アップロードする</div>
                </a>
                <ErrorMessage type="error" validationErrors={getErrors('song_vocal_on')} />
              </li>
            </>
          )}
          {needsOffvocalFiles && (
            <>
              { /* eslint-disable-next-line max-len */ }
              { /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */ }
              <li className={hasOffvocalFile ? 'ok' : ''} onClick={(e) => { cancelEvent(e); onUploadOffvocalAudio(); }}>
                <a href="/">
                  <b>歌なし音源</b>
                  <span>
                    wavまたはmp3形式。
                    <br />
                    150MB以内
                  </span>
                  <div className="label">
                    <p>{hasOffvocalFile ? '設定済み' : '未設定'}</p>
                  </div>
                  <div className="sub_button">アップロードする</div>
                </a>
                <ErrorMessage type="error" validationErrors={getErrors('song_vocal_off')} />
              </li>
            </>
          )}
          { /* eslint-disable-next-line max-len */ }
          { /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */ }
          <li className={hasLyrics ? 'ok' : ''} onClick={(e) => { cancelEvent(e); onUploadLyrics(); }}>
            <a href="/">
              <b>歌詞</b>
              <span>直接テキスト入力。</span>
              <div className="label">
                <p>{hasLyrics ? '設定済み' : '未設定'}</p>
              </div>
              <div className="sub_button">アップロードする</div>
            </a>
            <ErrorMessage type="error" validationErrors={getErrors('lyrics')} />
          </li>
        </ul>
        <ul>
          <li className="bt">
            <CustomButton type="cancel-split" onClick={() => onBack()}>戻る</CustomButton>
            <CustomButton type="default-split" disabled={disabled} onClick={() => onNext()}>確認画面へ</CustomButton>
          </li>
        </ul>
        <p className="text_info py-30 t-center">
          アップロードがうまくいかない場合は、
          {/* eslint-disable-next-line react/jsx-no-target-blank */}
          <a href="https://help.frekul.com/375/" target="_blank">ヘルプページ</a>
          をご覧ください。
        </p>
      </form>
    </>
  );
}
